import { Coord, Dimensions } from '@99designs/browser';

export interface CalculatePositionReturn {
    scaledImageSize: Dimensions;
    artboardSize: Dimensions;
    artboardOrigin: Coord;
    imageToScreen: ImageToScreenFn;
    screenToImage: ScreenToImageFn;
    pointerPosition: PointerPositionFn;
}

export type ScreenToImageFn = (Coord: Coord) => Coord;

export type ImageToScreenFn = (Coord: Coord) => Coord;

export type PointerPositionFn = (
    ref: React.RefObject<HTMLDivElement>,
    eventOrTouch: React.MouseEvent<HTMLDivElement> | React.Touch | MouseEvent | Touch
) => Coord | null;

// Calculate position of artboard element relative to document
// by iterating up through element offsetParent's
function getElementPosition(el: React.RefObject<HTMLDivElement>) {
    let element = el.current;
    if (!element) return;

    let left = element.offsetLeft - element.scrollLeft;
    let top = element.offsetTop - element.scrollTop;

    while (element) {
        left += element.offsetLeft - element.scrollLeft;
        top += element.offsetTop - element.scrollTop;
        element = element.offsetParent as HTMLDivElement;
    }

    return {
        left,
        top,
    };
}

function calculatePosition(
    imageSize: Dimensions,
    viewSize: Dimensions,
    scale: number
): CalculatePositionReturn {
    const scaledImageSize = {
        width: imageSize.width * scale,
        height: imageSize.height * scale,
    };
    // Artboard needs to be at least as big as the visible viewport to recieve mouse events for annotating / sketching etc
    const artboardSize = {
        width: Math.max(scaledImageSize.width, viewSize.width),
        height: Math.max(scaledImageSize.height, viewSize.height),
    };
    // Center the image inside the artboard
    const artboardOrigin = {
        x: (artboardSize.width - scaledImageSize.width) / 2,
        y: (artboardSize.height - scaledImageSize.height) / 2,
    };

    // Convert image coordinates to screen coordinates
    const imageToScreen = ({ x, y }: Coord) => ({
        x: x * scale + artboardOrigin.x,
        y: y * scale + artboardOrigin.y,
    });

    // Convert screen coordinates back to image coordinates (useful for mouse handlers)
    const screenToImage = ({ x, y }: Coord) => ({
        x: (x - artboardOrigin.x) / scale,
        y: (y - artboardOrigin.y) / scale,
    });

    const pointerPosition = (
        ref: React.RefObject<HTMLDivElement>,
        eventOrTouch: React.MouseEvent<HTMLDivElement> | React.Touch | MouseEvent | Touch
    ) => {
        const elementPosition: { left: number; top: number } | undefined = getElementPosition(ref);

        if (!elementPosition) {
            return null;
        }
        // Calculate mouse position relative to artboard element
        return screenToImage({
            x: eventOrTouch.clientX - elementPosition.left,
            y: eventOrTouch.clientY - elementPosition.top,
        });
    };

    return {
        artboardSize,
        artboardOrigin,
        scaledImageSize,
        imageToScreen,
        screenToImage,
        pointerPosition,
    };
}

export default calculatePosition;
